import { Tooltip } from '@/components/ui'
import {
  AccountTypeBadge,
  type AccountTypeBadgeVariant,
  type StatementStatus,
  StatementStatusBadge,
  VerifyingBadge,
} from '@/modules/projects'
import { STATEMENT_STATUSES } from '@/modules/projects/constants'
import { isSegmentHeldForTeam } from '@/modules/projects/status'
import type {
  ExcludedRange,
  Statement,
  StatementSegment,
} from '@/modules/projects/stores'
import {
  formatAccountType,
  formatDocumentClass,
  formatFailureReason,
} from '../../../labels'
import { periodLabel } from '../../reconciliation/helpers'
import { pageSpanLabel, uncoveredPageRanges } from './coverage'
import { RetrySegmentButton } from './RetrySegmentButton'

interface SegmentRow {
  kind: 'segment'
  startPage: number | null
  segment: StatementSegment
}

interface ExcludedRow {
  kind: 'excluded'
  startPage: number | null
  range: ExcludedRange
}

type ContentRow = SegmentRow | ExcludedRow

const orderedRows = (statement: Statement): ContentRow[] => {
  const segmentRows: ContentRow[] = (statement.segments ?? []).map(
    (segment) => ({ kind: 'segment', startPage: segment.startPage, segment }),
  )
  const excludedRows: ContentRow[] = (statement.excludedRanges ?? []).map(
    (range) => ({ kind: 'excluded', startPage: range.startPage, range }),
  )
  return [...segmentRows, ...excludedRows].sort(
    (first, second) =>
      (first.startPage ?? Number.MAX_SAFE_INTEGER) -
      (second.startPage ?? Number.MAX_SAFE_INTEGER),
  )
}

const SegmentRangeLine = ({ segment }: { segment: StatementSegment }) => {
  const isFailed = segment.status === STATEMENT_STATUSES.failed
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 py-1.5">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="whitespace-nowrap text-sm font-medium text-foreground">
          {pageSpanLabel(segment.startPage, segment.endPage)}
        </span>
        <AccountTypeBadge
          type={formatAccountType(segment.kind) as AccountTypeBadgeVariant}
          size="xs"
        />
        {isSegmentHeldForTeam(segment) ? (
          <VerifyingBadge />
        ) : (
          <StatementStatusBadge status={segment.status as StatementStatus} />
        )}
        {isFailed && segment.failureReason && (
          <span className="text-xs text-red-700">
            {formatFailureReason(segment.failureReason)}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {periodLabel(segment)}
        </span>
        {isFailed && <RetrySegmentButton segmentId={segment.id} />}
      </div>
    </div>
  )
}

const ExcludedRangeLine = ({ range }: { range: ExcludedRange }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 py-1.5 opacity-70">
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <span className="whitespace-nowrap text-sm font-medium text-muted-foreground">
        {pageSpanLabel(range.startPage, range.endPage)}
      </span>
      <span className="whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
        {formatDocumentClass(range.documentClass)}
      </span>
    </div>
    <Tooltip content="These pages were filtered out on purpose because they are not a bank statement.">
      <span className="cursor-default whitespace-nowrap text-xs text-muted-foreground">
        Set aside, not a bank statement
      </span>
    </Tooltip>
  </div>
)

export const StatementContentsPanel = ({
  statement,
}: {
  statement: Statement
}) => {
  const rows = orderedRows(statement)
  const gaps = uncoveredPageRanges(statement)

  return (
    <div className="border-t bg-neutral-50/60 px-6 py-3">
      <div className="divide-y divide-border/60">
        {rows.map((row, rowIndex) =>
          row.kind === 'segment' ? (
            <SegmentRangeLine
              key={`segment-${row.segment.id}`}
              segment={row.segment}
            />
          ) : (
            <ExcludedRangeLine
              key={`excluded-${rowIndex}-${row.range.startPage}-${row.range.endPage}`}
              range={row.range}
            />
          ),
        )}
      </div>
      {gaps.length > 0 && (
        <div className="mt-2 flex flex-col gap-0.5">
          {gaps.map((gap) => (
            <span
              key={`gap-${gap.start}-${gap.end}`}
              className="text-xs text-amber-700"
            >
              {pageSpanLabel(gap.start, gap.end)} not in any statement
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
